import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Button,
  IconButton,
  Card,
  Typography,
  Collapse,
  Menu,
  MenuItem,
} from '@mui/material';
import { KeyboardArrowRight, KeyboardArrowLeft, Sort, KeyboardArrowDown } from '@mui/icons-material';
import { useSelector } from 'react-redux';

function AllOrderTable() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedStatus, setSelectedStatus] = useState('Pending');
  const [selectedStrategies, setSelectedStrategies] = useState(['All']);
  const [expandedRow, setExpandedRow] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [sortBy, setSortBy] = useState('newest');
  
  const { orders } = useSelector((state) => state.transaction);

  const strategyScrollRef = React.useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const handleScrollStrategies = (direction) => {
    if (strategyScrollRef.current) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      strategyScrollRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleScroll = () => {
    if (strategyScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = strategyScrollRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  // Reset states when orders change
  useEffect(() => {
    setPage(0);
    setExpandedRow(null);
    setAnchorEl(null);
  }, [orders]);

  // First filter out MANAGER_DISCRETION orders
  const nonManagerDiscretionOrders = React.useMemo(() => 
    orders.filter(order => order.type !== 'MANAGER_DISCRETION' && order.isManagerDiscretion === false),
    [orders]
  );

  // Calculate counts for each status
  const statusCounts = React.useMemo(() => ({
    Pending: nonManagerDiscretionOrders.filter(order => order.status === 'PENDING').length,
    Executed: nonManagerDiscretionOrders.filter(order => order.status === 'COMPLETED').length,
    Failed: nonManagerDiscretionOrders.filter(order => order.status === 'FAILED').length
  }), [nonManagerDiscretionOrders]);

  const statusTabs = [
    { label: 'Pending', count: statusCounts.Pending },
    { label: 'Executed', count: statusCounts.Executed },
    { label: 'Failed', count: statusCounts.Failed },
  ];

  // Get unique strategies
  const strategies = React.useMemo(() => 
    ['All', ...new Set(nonManagerDiscretionOrders
      .filter(order => order.strategy?.name)
      .map(order => order.strategy.name))],
    [nonManagerDiscretionOrders]
  );

  // Setup scroll listeners for strategy navigation
  useEffect(() => {
    const scrollContainer = strategyScrollRef.current;
    if (scrollContainer) {
      handleScroll(); // Check initial state
      scrollContainer.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      return () => {
        scrollContainer.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
      };
    }
  }, [strategies]);

  // Filter orders based on status and selected strategies
  const filteredOrders = React.useMemo(() => 
    nonManagerDiscretionOrders.filter(order => {
      const statusMatch = 
        (selectedStatus === 'Pending' && order.status === 'PENDING') ||
        (selectedStatus === 'Executed' && order.status === 'COMPLETED') ||
        (selectedStatus === 'Failed' && order.status === 'FAILED');

      const strategyMatch = 
        selectedStrategies.includes('All') || 
        selectedStrategies.includes(order.strategy?.name);

      return statusMatch && strategyMatch;
    }),
    [nonManagerDiscretionOrders, selectedStatus, selectedStrategies]
  );

  // Reset selected strategies when switching tabs
  useEffect(() => {
    setSelectedStrategies(['All']);
  }, [selectedStatus]);

  const handleStrategyClick = (strategy) => {
    if (strategy === 'All') {
      setSelectedStrategies(['All']);
    } else {
      const newSelected = selectedStrategies.includes('All') 
        ? [strategy]
        : selectedStrategies.includes(strategy)
          ? selectedStrategies.filter(s => s !== strategy)
          : [...selectedStrategies, strategy];
      
      setSelectedStrategies(newSelected.length ? newSelected : ['All']);
    }
  };

  // Function to handle expanding/collapsing asset info
  const handleAssetInfoClick = (orderId) => {
    setExpandedRow(expandedRow === orderId ? null : orderId);
  };

  const handleSortClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleSortClose = (value) => {
    if (value) {
      setSortBy(value);
    }
    setAnchorEl(null);
  };

  const renderTransactionDetails = (transactions) => {
    if (!transactions?.length) return null;

    return (
      <Box sx={{ 
        borderRadius: '8px',
        overflow: 'hidden',
        border: '1px solid #1344EB',
        backgroundColor:'#f9f9f9',
        padding:'0 12px'
      }}>
        <Table size="small" sx={{ 
          '& .MuiTableCell-root': {
            borderBottom: '1px solid #E0E0E0',
            padding: '12px 16px',
          },
          '& .MuiTableHead-root .MuiTableCell-root': {
            color: '#0D0C3A',
            fontWeight: '500 !important',
            fontSize: '18px !important',
            padding: '12px 16px',
            fontFamily: 'inherit !important',
            fontStyle: 'inherit',
            lineHeight: '24px',
            letterSpacing: '0.15px',
            textAlign:'center',
           
          },
          '& .MuiTableBody-root .MuiTableCell-root': {
            backgroundColor: 'white',
            color: '#787A99',

            fontSize: '16px',
            backgroundColor:'#f9f9f9',
          },
          '& .MuiTableRow-root:last-child .MuiTableCell-root': {
            borderBottom: '1px solid #E0E0E0'
          },
          '& .MuiTableHead-root .MuiTableCell-root:first-child': {
            textAlign:'left'
          },
          '& .MuiTableBody-root .MuiTableCell-root:first-child': {
            textAlign:'left',
            color: '#43436A'
          },
          '& .MuiTableBody-root .MuiTableCell-root:not(:first-child)': {
            textAlign:'center'
          }
        }}>
          <TableHead>
            <TableRow>
              <TableCell>Asset</TableCell>
              <TableCell>Market Price</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>Value ($)</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((transaction) => (
              <TableRow key={transaction.id}>
                <TableCell>{transaction.asset?.symbol || '-'}</TableCell>
                <TableCell>{transaction.price ? Number(transaction.price).toFixed(2) : '-'}</TableCell>
                <TableCell>{transaction.quantity != null ? Number(transaction.quantity)?.toFixed(3) : '-'}</TableCell>
                <TableCell>{transaction.usdtEquivalent ? Number(transaction.usdtEquivalent)?.toFixed(3) : '-'}</TableCell>
                <TableCell sx={{ textTransform: 'uppercase' }}>{transaction?.status || '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  return (
    <>
      {/* Filters Card */}
      <Card sx={{ 
        borderRadius: '8px',
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
        mb: 2
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          pt: 2
        }}>
          {/* Status Tabs */}
          <Box sx={{ display: 'flex', gap: 4 }}>
            {statusTabs.map(({ label, count }) => (
              <Box
                key={label}
                onClick={() => setSelectedStatus(label)}
                sx={{
                  pb: 2,
                  cursor: 'pointer',
                  position: 'relative',
                  color: selectedStatus === label ? '#2196F3' : 'inherit',
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '2px',
                    bgcolor: selectedStatus === label ? '#2196F3' : 'transparent'
                  }
                }}
              >
                <Typography variant="body2">
                  {`${label} (${count})`}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Sort Button */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button
              onClick={handleSortClick}
              startIcon={<Sort />}
              endIcon={<KeyboardArrowDown />}
              sx={{
                color: '#666666',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'transparent'
                }
              }}
            >
              Sort
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => handleSortClose()}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={() => handleSortClose('newest')}>
                Newest First
              </MenuItem>
              <MenuItem onClick={() => handleSortClose('oldest')}>
                Oldest First
              </MenuItem>
              <MenuItem onClick={() => handleSortClose('amountHigh')}>
                Amount (High to Low)
              </MenuItem>
              <MenuItem onClick={() => handleSortClose('amountLow')}>
                Amount (Low to High)
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        {/* Strategy Filters */}
        <Box sx={{ 
          position: 'relative',
          py: 1.5,
          px: 2,
          borderBottom: '1px solid #E0E0E0'
        }}>
          {showLeftArrow && (
            <IconButton 
              onClick={() => handleScrollStrategies('left')}
              sx={{ 
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                minWidth: 40, 
                height: 40,
                bgcolor: 'white',
                border: '1px solid #E0E0E0',
                borderRadius: '6px',
                boxShadow: '4px 0 8px rgba(0, 0, 0, 0.05)',
                zIndex: 1,
                '&:hover': { 
                  bgcolor: '#F5F5F5',
                  borderColor: '#E0E0E0'
                }
              }}
            >
              <KeyboardArrowLeft />
            </IconButton>
          )}
          <Box 
            ref={strategyScrollRef}
            sx={{ 
              display: 'flex', 
              gap: 1,
              overflowX: 'auto',
              alignItems: 'center',
              px: showLeftArrow ? 6 : 0,
              pr: showRightArrow ? 6 : 0,
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            {strategies.map((strategy) => (
              <Button
                key={strategy}
                onClick={() => handleStrategyClick(strategy)}
                sx={{
                  py: 1,
                  px: 2.5,
                  border: '1px solid #E0E0E0',
                  borderRadius: '6px',
                  bgcolor: selectedStrategies.includes(strategy) ? '#EEF2FF' : 'white',
                  color: selectedStrategies.includes(strategy) ? '#0066FF' : '#666666',
                  fontSize: '14px',
                  fontWeight: 500,
                  textTransform: 'none',
                  minHeight: '40px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  '&:hover': {
                    bgcolor: selectedStrategies.includes(strategy) ? '#EEF2FF' : '#F5F5F5',
                    borderColor: selectedStrategies.includes(strategy) ? '#0066FF' : '#E0E0E0',
                  }
                }}
              >
                {strategy}
              </Button>
            ))}
          </Box>
          {showRightArrow && (
            <IconButton 
              onClick={() => handleScrollStrategies('right')}
              sx={{ 
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                minWidth: 40, 
                height: 40,
                bgcolor: 'white',
                border: '1px solid #E0E0E0',
                borderRadius: '6px',
                boxShadow: '-4px 0 8px rgba(0, 0, 0, 0.05)',
                zIndex: 1,
                '&:hover': { 
                  bgcolor: '#F5F5F5',
                  borderColor: '#E0E0E0'
                }
              }}
            >
              <KeyboardArrowRight />
            </IconButton>
          )}
        </Box>
      </Card>

      {/* Table Card */}
      <Card sx={{ 
        borderRadius: '8px',
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
        backgroundColor: '#FFFFFF',
      }}>
        <TableContainer sx={{ 
          overflow: 'visible' 
        }}>
          <Table sx={{
            '& .MuiTableCell-root': {
              borderBottom: 'none',
              width: '14.28%' 
            },
            '& .MuiTableRow-root': {
              borderBottom: 'none'
            }
          }}>
            <TableHead>
              <TableRow>
                <TableCell align="left" sx={{ color: '#666666'}}>Investor Email</TableCell>
                <TableCell align="center" sx={{ color: '#666666' }}>Strategy Name</TableCell>
                <TableCell align="center" sx={{ color: '#666666' }}>Amount ($)</TableCell>
                <TableCell align="center" sx={{ color: '#666666' }}>Type</TableCell>
                <TableCell align="center" sx={{ color: '#666666' }}>Status</TableCell>
                <TableCell align="center" sx={{ color: '#666666' }}>Date of Creation</TableCell>
                <TableCell align="center" sx={{ color: '#666666' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'right', gap: 1 }}>
                    Actions
                    <IconButton size="small">
                      <Sort />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredOrders
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((order) => (
                  <React.Fragment key={order.id}>
                    <TableRow>
                      <TableCell align="left">{order.userRole?.user?.email || '-'}</TableCell>
                      <TableCell align="center">{order.strategy?.name || '-'}</TableCell>
                      <TableCell align="center">{order.amount ? order.amount.toFixed(2) : '-'}</TableCell>
                      <TableCell align="center">{order.type || '-'}</TableCell>
                      <TableCell align="center">{order.status || '-'}</TableCell>
                      <TableCell align="center">{order.createdAt ? new Date(order.createdAt).toLocaleString() : '-'}</TableCell>
                      <TableCell align="right">
                        {order.transactions?.length > 0 ? (
                          <Button
                            sx={{
                              textTransform: 'none',
                              color: '#2196F3',
                              fontWeight: 'bold',
                              '&:hover': {
                                backgroundColor: 'transparent',
                                textDecoration: 'underline'
                              }
                            }}
                            onClick={() => handleAssetInfoClick(order.id)}
                          >
                            {expandedRow === order.id ? 'Hide Asset Info' : 'See Asset Info'}
                          </Button>
                        ) : (
                          <Button
                            disabled
                            sx={{
                              textTransform: 'none',
                              color: '#666666',
                              opacity: 0.5,
                              cursor: 'not-allowed'
                            }}
                          >
                            See Asset Info
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                    {order.transactions?.length > 0 && (
                      <TableRow>
                        <TableCell 
                          colSpan={7} 
                          sx={{ 
                            py: 0,
                            borderBottom: expandedRow === order.id ? '1px solid #E0E0E0' : 'none',
                            
                          }}
                        >
                          <Collapse in={expandedRow === order.id} timeout="auto" unmountOnExit >
                            <Box sx={{ 
                              py: 2,
                              px: 1,
                              overflow: 'visible',
                              backgroundColor: '#ffffff'
                            }}>
                              {renderTransactionDetails(order.transactions)} 
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2,
          // borderTop: '1px solid #E0E0E0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            Rows per page:
            <select
              value={rowsPerPage}
              onChange={(e) => setRowsPerPage(Number(e.target.value))}
              style={{ 
                marginLeft: '8px', 
                padding: '4px',
                border: '1px solid #E0E0E0',
                borderRadius: '4px'
              }}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
            </select>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            >
              <KeyboardArrowLeft />
            </IconButton>
            <Box>
              {page + 1} of {Math.ceil(filteredOrders.length / rowsPerPage)}
            </Box>
            <IconButton
              disabled={page >= Math.ceil(filteredOrders.length / rowsPerPage) - 1}
              onClick={() => setPage(page + 1)}
            >
              <KeyboardArrowRight />
            </IconButton>
          </Box>
        </Box>
      </Card>
    </>
  );
}

export default AllOrderTable;
